import React, { useState, useEffect } from 'react';
import { MonthlySchedule, ScheduledEvent, EventCategory, ScheduleTheme } from '../types';
import { apiClient, ApiClientError } from '../services/api-client';
import { UserWish } from '../services/mock-api';
import { useLoading } from '../hooks/useLoading';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import RestaurantGalleryModal from './RestaurantGalleryModal';
import './ScheduleCalendar.css';

interface ScheduleCalendarProps {}

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = () => {
  const [schedule, setSchedule] = useState<MonthlySchedule | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [editingEvent, setEditingEvent] = useState<{ date: Date; event: ScheduledEvent } | null>(null);
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);
  const [availableThemes, setAvailableThemes] = useState<ScheduleTheme[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { loading, withLoading, setLoading } = useLoading();
  
  const [draggedEvent, setDraggedEvent] = useState<{ day: number; event: ScheduledEvent } | null>(null);
  const [dragOverDay, setDragOverDay] = useState<number | null>(null);
  
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [showRestaurantGallery, setShowRestaurantGallery] = useState(false);

  useEffect(() => {
    loadSchedule();
  }, [year, month]);

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      const themes = await withLoading('loadThemes', () => 
        apiClient.getAvailableThemes()
      );
      setAvailableThemes(themes);
    } catch (err) {
      console.error('Failed to load themes:', err);
    }
  };

  const loadSchedule = async () => {
    try {
      const loadedSchedule = await withLoading('loadSchedule', () => 
        apiClient.getSchedule(year, month)
      );
      setSchedule(loadedSchedule);
      setError(null);
    } catch (err) {
      console.error('Failed to load schedule:', err);
      if (err instanceof ApiClientError && err.code === 'NOT_FOUND') {
        setSchedule(null);
        setError(null);
      } else {
        setError(err instanceof ApiClientError ? err.message : 'スケジュールの読み込みに失敗しました');
      }
    }
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const generateCalendarDays = (): (number | null)[] => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days: (number | null)[] = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const getEventForDate = (day: number): ScheduledEvent | null => {
    if (!schedule) return null;
    
    const targetDate = new Date(year, month - 1, day);
    return schedule.events.find(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === day &&
             eventDate.getMonth() === month - 1 &&
             eventDate.getFullYear() === year;
    }) || null;
  };

  const handleDragStart = (e: React.DragEvent, day: number, event: ScheduledEvent) => {
    setDraggedEvent({ day, event });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', day.toString());
    
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedEvent(null);
    setDragOverDay(null);
    
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDragOver = (e: React.DragEvent, day: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    setDragOverDay(null);
  };

  const handleDrop = (e: React.DragEvent, targetDay: number) => {
    e.preventDefault();
    
    if (!draggedEvent) return;

    const sourceDay = draggedEvent.day;
    
    if (sourceDay === targetDay) {
      setDraggedEvent(null);
      setDragOverDay(null);
      return;
    }

    swapEvents(sourceDay, targetDay);
    
    setDraggedEvent(null);
    setDragOverDay(null);
  };

  const swapEvents = async (sourceDay: number, targetDay: number) => {
    if (!schedule) return;

    const sourceEvent = getEventForDate(sourceDay);
    const targetEvent = getEventForDate(targetDay);

    if (!sourceEvent) return;

    const updatedEvents = schedule.events.map(event => {
      const eventDate = new Date(event.date);
      const eventDay = eventDate.getDate();
      const eventMonth = eventDate.getMonth();
      const eventYear = eventDate.getFullYear();

      if (eventMonth !== month - 1 || eventYear !== year) {
        return event;
      }

      if (eventDay === sourceDay) {
        return {
          ...event,
          date: new Date(year, month - 1, targetDay)
        };
      } else if (eventDay === targetDay && targetEvent) {
        return {
          ...event,
          date: new Date(year, month - 1, sourceDay)
        };
      }

      return event;
    });

    const updatedSchedule = {
      ...schedule,
      events: updatedEvents
    };

    try {
      await withLoading('saveSchedule', () => apiClient.saveSchedule(updatedSchedule));
      setSchedule(updatedSchedule);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'イベントの交換に失敗しました');
    }
  };

  const handleEventClick = (day: number, event: ScheduledEvent) => {
    const date = new Date(year, month - 1, day);
    setEditingEvent({ date, event });
  };

  const saveEditedEvent = async (updatedEvent: ScheduledEvent) => {
    if (!schedule || !editingEvent) return;

    try {
      const updatedSchedule = {
        ...schedule,
        events: schedule.events.map(event => 
          event.date.getTime() === editingEvent.date.getTime() ? updatedEvent : event
        )
      };

      await withLoading('saveSchedule', () => apiClient.saveSchedule(updatedSchedule));
      setSchedule(updatedSchedule);
      setEditingEvent(null);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'イベントの保存に失敗しました');
    }
  };

  const handleThemeChange = async (themeId: string) => {
    if (!schedule) return;

    try {
      const updatedSchedule = await withLoading('updateTheme', () =>
        apiClient.updateScheduleTheme(schedule.id, themeId)
      );
      setSchedule(updatedSchedule);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'テーマの更新に失敗しました');
    }
  };

  const handleImageUpload = async (file: File, imageType: 'header' | 'background') => {
    if (!schedule) {
      alert('エラー: スケジュールが見つかりません');
      return;
    }

    try {
      alert('画像アップロード中です。しばらくお待ちください...');
      
      setLoading('uploadImage', true);
      
      const imageUrl = await apiClient.uploadScheduleImage(schedule.id, file, imageType);
      
      alert('画像のアップロードが完了しました: ' + file.name);
      
      const updatedSchedule = {
        ...schedule,
        customImages: {
          ...schedule.customImages,
          [imageType === 'header' ? 'headerImage' : 'backgroundImage']: imageUrl
        }
      };
      
      setSchedule(updatedSchedule);
      setError(null);
      
      setTimeout(() => {
        loadSchedule();
      }, 100);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError ? err.message : '画像のアップロードに失敗しました';
      alert('エラー: ' + errorMessage);
      setError(errorMessage);
    } finally {
      setLoading('uploadImage', false);
    }
  };

  const handleGenerateSchedule = async () => {
    try {
      const historicalYear = year - 1;
      const historicalMonth = month;
      
      const generatedSchedule = await withLoading('generateSchedule', () =>
        apiClient.generateSchedule(year, month, historicalYear, historicalMonth)
      );
      
      setSchedule(generatedSchedule);
      setError(null);
      
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'スケジュール生成に失敗しました');
    }
  };

  const calendarDays = generateCalendarDays();
  const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

  return (
    <div className="schedule-calendar">
      <div className={`calendar-month-header month-${month}`}>
        <h1>{year}年 {monthNames[month - 1]}</h1>
        <div className="month-decoration">
          {month === 1 && '🎍 新年 🎍'}
          {month === 2 && '💝 バレンタイン 💝'}
          {month === 3 && '🌸 桜の季節 🌸'}
          {month === 4 && '🌷 新生活 🌷'}
          {month === 5 && '🎏 こどもの日 🎏'}
          {month === 6 && '☔ 梅雨 ☔'}
          {month === 7 && '🎋 七夕・夏祭り 🎋'}
          {month === 8 && '🎆 花火大会 🎆'}
          {month === 9 && '🌕 お月見 🌕'}
          {month === 10 && '🎃 ハロウィン 🎃'}
          {month === 11 && '🍂 紅葉 🍂'}
          {month === 12 && '🎄 クリスマス 🎄'}
        </div>
      </div>
      
      <div className="calendar-header">
        <h2>月間スケジュール</h2>
        
        <div className="date-controls">
          <select 
            value={year} 
            onChange={(e) => setYear(parseInt(e.target.value))}
            disabled={loading.generateSchedule}
          >
            {Array.from({ length: 10 }, (_, i) => {
              const y = new Date().getFullYear() - 2 + i;
              return <option key={y} value={y}>{y}年</option>;
            })}
          </select>
          
          <select 
            value={month} 
            onChange={(e) => setMonth(parseInt(e.target.value))}
            disabled={loading.generateSchedule}
          >
            {monthNames.map((name, index) => (
              <option key={index + 1} value={index + 1}>{name}</option>
            ))}
          </select>
          
          <button 
            onClick={handleGenerateSchedule}
            disabled={loading.generateSchedule || loading.loadSchedule}
            className="generate-btn"
          >
            {loading.generateSchedule ? 'スケジュール生成中...' : 'スケジュール生成'}
          </button>

          {schedule && (
            <button 
              onClick={() => setShowThemeCustomizer(true)}
              disabled={loading.updateTheme || loading.uploadImage}
              className="theme-btn"
            >
              テーマ変更
            </button>
          )}

          <button 
            onClick={async () => {
              try {
                const monthlyImages = await apiClient.getMonthlyImages(month);
                
                let message = `${month}月の画像状況\n\n`;
                message += `ヘッダー画像: ${monthlyImages.header ? 'アップロード済み ✅' : '未アップロード ❌'}\n`;
                message += `背景画像: ${monthlyImages.background ? 'アップロード済み ✅' : '未アップロード ❌'}\n\n`;
                
                if (monthlyImages.header) {
                  message += `ヘッダー画像データ: ${monthlyImages.header.length}文字\n`;
                }
                if (monthlyImages.background) {
                  message += `背景画像データ: ${monthlyImages.background.length}文字\n`;
                }
                
                message += '\nスケジュール生成時に、アップロード済み画像が自動的に使用されます。';
                
                alert(message);
              } catch (error) {
                alert('月間画像の取得に失敗しました: ' + (error instanceof Error ? error.message : 'Unknown error'));
              }
            }}
            style={{ marginLeft: '10px', padding: '8px 12px', fontSize: '12px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px' }}
          >
            {month}月画像状況確認
          </button>
        </div>

      {error && (
        <ErrorMessage 
          message={error} 
          onDismiss={() => setError(null)} 
        />
      )}

      {loading.loadSchedule && (
        <LoadingSpinner message="スケジュールを読み込み中..." />
      )}
      </div>

      <div 
        className={`calendar-grid month-${month}`}
        style={{
          '--theme-primary': schedule?.theme?.primaryColor || '#007bff',
          '--theme-secondary': schedule?.theme?.secondaryColor || '#6c757d',
          backgroundImage: schedule?.customImages?.backgroundImage ? `url(${schedule.customImages.backgroundImage})` : undefined
        } as React.CSSProperties}
      >
        <div className="calendar-weekdays">
          <div className="weekday">日</div>
          <div className="weekday">月</div>
          <div className="weekday">火</div>
          <div className="weekday">水</div>
          <div className="weekday">木</div>
          <div className="weekday">金</div>
          <div className="weekday">土</div>
        </div>

        <div className="calendar-days">
          {calendarDays.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="calendar-day empty"></div>;
            }

            const event = getEventForDate(day);
            const isDragOver = dragOverDay === day;
            const isDragging = draggedEvent?.day === day;
            
            const getCategoryClassName = (category: EventCategory): string => {
              switch (category) {
                case EventCategory.Work:
                  return 'work';
                case EventCategory.HealthBeauty:
                  return 'healthbeauty';
                case EventCategory.Hobby:
                  return 'hobby';
                case EventCategory.Life:
                  return 'life';
                default:
                  return '';
              }
            };
            
            return (
              <div 
                key={`day-${year}-${month}-${day}`} 
                className={`calendar-day ${isDragOver ? 'drag-over' : ''} ${isDragging ? 'dragging' : ''}`}
                onDragOver={(e) => handleDragOver(e, day)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, day)}
              >
                <div className="day-number">{day}</div>
                {event && (
                  <div 
                    className={`event-item ${getCategoryClassName(event.eventCategory)}`}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, day, event)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleEventClick(day, event)}
                    title="クリックして編集、ドラッグして他の日に移動できます"
                  >
                    <div className="event-content">
                      <div className="event-name">{event.eventName}</div>
                      <div className="event-category">{event.eventCategory}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {schedule && (
        <div className="schedule-info">
          <p>生成日時: {new Date(schedule.generatedAt).toLocaleString('ja-JP')}</p>
          <p>イベント数: {schedule.events.length}</p>
        </div>
      )}

      <button 
        className="activities-button"
        onClick={() => setShowActivitiesModal(true)}
        title="やりたいこと"
      >
        やりたいこと
      </button>

      {showActivitiesModal && (
        <ActivitiesModal
          onClose={() => setShowActivitiesModal(false)}
          onSelectRestaurants={() => {
            setShowActivitiesModal(false);
            setShowRestaurantGallery(true);
          }}
        />
      )}

      {showRestaurantGallery && (
        <RestaurantGalleryModal
          onClose={() => setShowRestaurantGallery(false)}
          onBack={() => {
            setShowRestaurantGallery(false);
            setShowActivitiesModal(true);
          }}
        />
      )}

      {editingEvent && (
        <EventEditModal
          event={editingEvent.event}
          onSave={saveEditedEvent}
          onCancel={() => setEditingEvent(null)}
        />
      )}
    </div>
  );
};

interface ThemeCustomizerModalProps {
  schedule: MonthlySchedule;
  availableThemes: ScheduleTheme[];
  onThemeChange: (themeId: string) => void;
  onImageUpload: (file: File, imageType: 'header' | 'background') => void;
  onClose: () => void;
  loading: any;
}

const ThemeCustomizerModal: React.FC<ThemeCustomizerModalProps> = ({
  schedule,
  availableThemes,
  onThemeChange,
  onImageUpload,
  onClose,
  loading
}) => {
  const [selectedTheme, setSelectedTheme] = useState(schedule.theme?.id || '');

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, imageType: 'header' | 'background') => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('画像ファイルを選択してください。選択されたファイル: ' + file.type);
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert('ファイルサイズは5MB以下にしてください。現在のサイズ: ' + Math.round(file.size / 1024 / 1024 * 100) / 100 + 'MB');
        return;
      }
      
      alert('画像アップロードを開始します: ' + file.name);
      onImageUpload(file, imageType);
      
      event.target.value = '';
    }
  };

  const handleMonthlyImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, imageType: 'header' | 'background') => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('画像ファイルを選択してください。選択されたファイル: ' + file.type);
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert('ファイルサイズは5MB以下にしてください。現在のサイズ: ' + Math.round(file.size / 1024 / 1024 * 100) / 100 + 'MB');
        return;
      }
      
      try {
        alert('月間デフォルト画像のアップロードを開始します: ' + file.name);
        
        const result = await apiClient.uploadMonthlyImage(schedule.month, file, imageType);
        
        let successMessage = `${schedule.month}月のデフォルト${imageType === 'header' ? 'ヘッダー' : '背景'}画像を設定しました！\n\n`;
        successMessage += `ファイル名: ${file.name}\n`;
        successMessage += `ファイルサイズ: ${Math.round(file.size / 1024)}KB\n`;
        successMessage += `データサイズ: ${result.length}文字\n\n`;
        successMessage += `今後${schedule.month}月のスケジュール生成時に、この画像が自動的に使用されます。`;
        
        alert(successMessage);
        
        event.target.value = '';
      } catch (error) {
        alert(`画像のアップロードに失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content theme-customizer">
        <h3>スケジュールテーマ設定</h3>
        
        <div className="theme-section">
          <h4>季節テーマ選択</h4>
          <div className="theme-grid">
            {availableThemes.map(theme => (
              <div
                key={theme.id}
                className={`theme-option ${selectedTheme === theme.id ? 'selected' : ''}`}
                onClick={() => setSelectedTheme(theme.id)}
                style={{
                  backgroundColor: theme.primaryColor,
                  color: theme.secondaryColor
                }}
              >
                <div className="theme-name">{theme.name}</div>
                <div className="theme-colors">
                  <div 
                    className="color-swatch" 
                    style={{ backgroundColor: theme.primaryColor }}
                  />
                  <div 
                    className="color-swatch" 
                    style={{ backgroundColor: theme.secondaryColor }}
                  />
                </div>
              </div>
            ))}
          </div>
          
          {selectedTheme !== schedule.theme?.id && (
            <button 
              onClick={() => onThemeChange(selectedTheme)}
              disabled={loading.updateTheme}
              className="apply-theme-btn"
            >
              {loading.updateTheme ? 'テーマ適用中...' : 'テーマを適用'}
            </button>
          )}
        </div>

        <div className="image-section">
          <h4>このスケジュールの画像</h4>
          
          <div className="image-upload-group">
            <label>ヘッダー画像</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'header')}
              disabled={loading.uploadImage}
            />
            {schedule.customImages?.headerImage && (
              <div className="image-preview">
                <img 
                  src={schedule.customImages.headerImage} 
                  alt="ヘッダー画像プレビュー"
                  style={{ maxWidth: '200px', maxHeight: '100px' }}
                />
                <p>現在のヘッダー画像が設定されています</p>
              </div>
            )}
          </div>

          <div className="image-upload-group">
            <label>背景画像</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'background')}
              disabled={loading.uploadImage}
            />
            {schedule.customImages?.backgroundImage && (
              <div className="image-preview">
                <img 
                  src={schedule.customImages.backgroundImage} 
                  alt="背景画像プレビュー"
                  style={{ maxWidth: '200px', maxHeight: '100px' }}
                />
                <p>現在の背景画像が設定されています</p>
              </div>
            )}
          </div>
        </div>

        <div className="monthly-image-section">
          <h4>{schedule.month}月のデフォルト画像設定</h4>
          <p className="section-description">
            ここで設定した画像は、{schedule.month}月のスケジュール生成時に自動的に使用されます
          </p>
          
          <div className="image-upload-group">
            <label>{schedule.month}月のデフォルトヘッダー画像</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleMonthlyImageUpload(e, 'header')}
              disabled={loading.uploadImage}
            />
            <small>この画像は今後の{schedule.month}月スケジュール生成時に自動適用されます</small>
          </div>

          <div className="image-upload-group">
            <label>{schedule.month}月のデフォルト背景画像</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleMonthlyImageUpload(e, 'background')}
              disabled={loading.uploadImage}
            />
            <small>この画像は今後の{schedule.month}月スケジュール生成時に自動適用されます</small>
          </div>
          
          <button 
            onClick={async () => {
              try {
                const monthlyImages = await apiClient.getMonthlyImages(schedule.month);
                alert(`${schedule.month}月のデフォルト画像状況をコンソールに出力しました。F12キーを押してコンソールを確認してください。`);
              } catch (error) {
                alert('月間画像の取得に失敗しました');
              }
            }}
            style={{ marginTop: '10px', padding: '5px 10px', fontSize: '12px' }}
          >
            月間画像状況をデバッグ
          </button>
        </div>

        <div className="uploaded-images-status">
          <h4>アップロード済み画像の状況</h4>
          <UploadedImagesStatus month={schedule.month} />
        </div>
        
        <div className="modal-actions">
          <button onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  );
};

interface UploadedImagesStatusProps {
  month: number;
}

const UploadedImagesStatus: React.FC<UploadedImagesStatusProps> = ({ month }) => {
  const [monthlyImages, setMonthlyImages] = useState<{ header?: string; background?: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMonthlyImages();
  }, [month]);

  const loadMonthlyImages = async () => {
    setLoading(true);
    try {
      const images = await apiClient.getMonthlyImages(month);
      setMonthlyImages(images);
    } catch (error) {
      setMonthlyImages(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>読み込み中...</div>;
  }

  return (
    <div className="uploaded-images-status">
      <div className="status-item">
        <span className="status-label">ヘッダー画像:</span>
        <span className={`status-value ${monthlyImages?.header ? 'uploaded' : 'not-uploaded'}`}>
          {monthlyImages?.header ? '✅ アップロード済み' : '❌ 未アップロード'}
        </span>
        {monthlyImages?.header && (
          <div className="image-preview-small">
            <img 
              src={monthlyImages.header} 
              alt="ヘッダー画像プレビュー"
              style={{ maxWidth: '100px', maxHeight: '50px', marginLeft: '10px' }}
            />
          </div>
        )}
      </div>
      
      <div className="status-item">
        <span className="status-label">背景画像:</span>
        <span className={`status-value ${monthlyImages?.background ? 'uploaded' : 'not-uploaded'}`}>
          {monthlyImages?.background ? '✅ アップロード済み' : '❌ 未アップロード'}
        </span>
        {monthlyImages?.background && (
          <div className="image-preview-small">
            <img 
              src={monthlyImages.background} 
              alt="背景画像プレビュー"
              style={{ maxWidth: '100px', maxHeight: '50px', marginLeft: '10px' }}
            />
          </div>
        )}
      </div>
      
      <button 
        onClick={loadMonthlyImages}
        style={{ marginTop: '10px', padding: '5px 10px', fontSize: '12px' }}
      >
        状況を更新
      </button>
    </div>
  );
};

interface EventEditModalProps {
  event: ScheduledEvent;
  onSave: (event: ScheduledEvent) => void;
  onCancel: () => void;
}

const EventEditModal: React.FC<EventEditModalProps> = ({ event, onSave, onCancel }) => {
  const [editedEvent, setEditedEvent] = useState<ScheduledEvent>(event);

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>イベント編集</h3>
        <div className="form-group">
          <label>イベント名</label>
          <input
            type="text"
            value={editedEvent.eventName}
            onChange={(e) => setEditedEvent({ ...editedEvent, eventName: e.target.value })}
          />
        </div>
        <div className="modal-actions">
          <button onClick={() => onSave(editedEvent)}>保存</button>
          <button onClick={onCancel}>キャンセル</button>
        </div>
      </div>
    </div>
  );
};

interface ActivitiesModalProps {
  onClose: () => void;
  onSelectRestaurants: () => void;
}

const ActivitiesModal: React.FC<ActivitiesModalProps> = ({ onClose, onSelectRestaurants }) => {
  const [wishes, setWishes] = useState<UserWish[]>([]);
  const [userName, setUserName] = useState('');
  const [wishText, setWishText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    loadWishes();
  }, []);

  const loadWishes = async () => {
    try {
      const data = await apiClient.getUserWishes();
      setWishes(data);
    } catch (err) {
      console.error('Failed to load wishes:', err);
    }
  };

  const handleAddWish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userName.trim() || !wishText.trim()) return;

    setSubmitting(true);
    try {
      const newWish = await apiClient.addUserWish(userName.trim(), wishText.trim());
      setWishes(prev => [...prev, newWish]);
      setUserName('');
      setWishText('');
      setMessage('✅ やりたいことを登録しました。次回のスケジュール生成時に優先されます。');
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setMessage('❌ 登録に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteWish = async (wishId: string) => {
    try {
      await apiClient.deleteUserWish(wishId);
      setWishes(prev => prev.filter(w => w.id !== wishId));
    } catch (err) {
      console.error('Failed to delete wish:', err);
    }
  };

  const handleComingSoon = (activityName: string) => {
    alert(`${activityName}機能は現在開発中です。近日公開予定です！`);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content activities-modal" onClick={(e) => e.stopPropagation()}>
        <h2>やりたいこと</h2>
        <p className="modal-subtitle">興味のある活動を選んでください</p>
        
        <div className="activities-grid">
          <button 
            className="activity-card restaurant-card"
            onClick={onSelectRestaurants}
          >
            <div className="activity-icon">🍽️</div>
            <div className="activity-name">お食事</div>
            <div className="activity-description">全国各地のレストラン</div>
          </button>

          <button 
            className="activity-card outing-card"
            onClick={() => handleComingSoon('お出かけ')}
          >
            <div className="activity-icon">🚶</div>
            <div className="activity-name">お出かけ</div>
            <div className="activity-description">開発中</div>
          </button>

          <button 
            className="activity-card exercise-card"
            onClick={() => handleComingSoon('運動')}
          >
            <div className="activity-icon">🏃</div>
            <div className="activity-name">運動</div>
            <div className="activity-description">開発中</div>
          </button>
        </div>

        {/* やりたいこと入力フォーム */}
        <div className="wish-input-section">
          <h3>🌟 やりたいことを登録</h3>
          <p className="wish-description">
            利用者名とやりたいことを入力すると、その利用者が来る曜日のスケジュールに優先的に反映されます。
          </p>
          <form className="wish-form" onSubmit={handleAddWish}>
            <div className="wish-form-row">
              <input
                type="text"
                className="wish-input"
                placeholder="利用者名"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                disabled={submitting}
              />
              <input
                type="text"
                className="wish-input wish-input-wide"
                placeholder="やりたいこと（例：カラオケ、お花見）"
                value={wishText}
                onChange={(e) => setWishText(e.target.value)}
                disabled={submitting}
              />
              <button
                type="submit"
                className="wish-submit-btn"
                disabled={submitting || !userName.trim() || !wishText.trim()}
              >
                {submitting ? '登録中...' : '登録'}
              </button>
            </div>
          </form>

          {message && (
            <div className="wish-message">{message}</div>
          )}

          {wishes.length > 0 && (
            <div className="wish-list">
              <h4>登録済みのやりたいこと</h4>
              {wishes.map(wish => (
                <div key={wish.id} className="wish-item">
                  <span className="wish-user">👤 {wish.userName}</span>
                  <span className="wish-text">→ {wish.wishText}</span>
                  <button
                    className="wish-delete-btn"
                    onClick={() => handleDeleteWish(wish.id)}
                    title="削除"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="close-button">閉じる</button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleCalendar;
